import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Copy, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../../styling/stylingHelper';
import { getTableFormattedDate } from '../../../utils/dateUtils';
import { AdminLayout } from './AdminLayout';
import { MobileCardField, MobileCardList, ResponsiveTable } from '../../shared/mobile/ResponsiveTable';
import { isValidEmail } from '../bookDemo/bookDemoConstants';
import type { RevenueRange } from '../../../services/demoService';
import {
  createDemoPrefill,
  deleteDemoPrefill,
  deleteDemoRequest,
  getDemoPrefills,
  getDemoRequests,
  updateDemoPrefill,
  type DemoPrefillSummary,
  type DemoRequestSummary,
} from '../../../services/admin/demoLinksService';

const REVENUE_RANGE_OPTIONS: RevenueRange[] = ['lt1m', '1to4m', '4to10m', '10to25m', 'gt25m'];

const MAX_SLUG_LENGTH = 40;

/** Both lists grow forever, so they page incrementally (repo rule for large lists). */
const PAGE_SIZE = 25;

/**
 * Mirrors the server's `sanitizeSlug`, minus the dash trimming — trimming on every keystroke
 * would stop you typing "van-" before "valk". The trim happens on submit.
 */
const sanitizeSlugWhileTyping = (raw: string) =>
  raw.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').slice(0, MAX_SLUG_LENGTH);

const finalizeSlug = (raw: string) => sanitizeSlugWhileTyping(raw).replace(/^-|-$/g, '');

const formInputClass = cn(
  'w-full px-3 py-2 rounded-lg border',
  'border-gray-300 dark:border-gray-600',
  'bg-white dark:bg-gray-700',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  primaryText,
  smallTextSize
);

const primaryButtonClass = cn(
  'px-4 py-2 rounded-lg transition-colors',
  'bg-purple-600 dark:bg-purple-600 text-white dark:text-white',
  'hover:bg-purple-700 dark:hover:bg-purple-700',
  'disabled:opacity-40 disabled:cursor-not-allowed',
  smallTextSize,
  semiBoldText
);

const secondaryButtonClass = cn(
  'px-4 py-2 rounded-lg transition-colors',
  'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
  'disabled:opacity-40 disabled:cursor-not-allowed',
  primaryText,
  smallTextSize,
  semiBoldText
);

const headerCellClass = cn(tertiaryText, 'text-xs font-medium text-left px-3 py-2 whitespace-nowrap');
const cellClass = cn(primaryText, smallTextSize, 'px-3 py-2 align-top');

interface PrefillFormState {
  slug: string;
  businessName: string;
  restaurantCount: string;
  restaurantCount20Plus: boolean;
  revenueRange: '' | RevenueRange;
  contactName: string;
  email: string;
  phone: string;
  notes: string;
}

const EMPTY_FORM: PrefillFormState = {
  slug: '',
  businessName: '',
  restaurantCount: '',
  restaurantCount20Plus: false,
  revenueRange: '',
  contactName: '',
  email: '',
  phone: '',
  notes: '',
};

/** Copy-to-clipboard button that confirms itself for two seconds. */
const CopyButton = ({ value, label, copiedLabel }: { value: string; label: string; copiedLabel: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy demo link', error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? copiedLabel : label}
      title={copied ? copiedLabel : label}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded-lg transition-colors shrink-0',
        'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
        primaryText,
        'text-xs'
      )}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? copiedLabel : null}
    </button>
  );
};

const UsedBadge = ({ usedAt, usedLabel, unusedLabel }: { usedAt: string | null; usedLabel: string; unusedLabel: string }) => (
  <span
    className={cn(
      'inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
      usedAt
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
    )}
  >
    {usedAt ? usedLabel : unusedLabel}
  </span>
);

const StatusChip = ({ status, label }: { status: DemoRequestSummary['status']; label: string }) => (
  <span
    className={cn(
      'inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
      status === 'booked'
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
    )}
  >
    {label}
  </span>
);

/**
 * Header button + rotating chevron, matching the expand/collapse idiom on AdminSetupPage.
 *
 * `action` renders a pill INSIDE the header button (a span, never a nested <button> — that is
 * invalid HTML), so it reads as the call to action while still just toggling the section.
 */
const CollapsibleSection = ({
  title,
  isOpen,
  onToggle,
  action,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <section className={cn(cardClass, 'overflow-hidden')}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className={cn(
        'w-full px-4 lg:px-6 py-3 flex items-center justify-between gap-3 text-left',
        'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors'
      )}
    >
      <span className={cn(primaryText, 'text-lg', semiBoldText, 'min-w-0')}>{title}</span>
      <span className="flex items-center gap-2 shrink-0">
        {action}
        <ChevronDown
          className={cn('w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500 transition-transform', isOpen && 'rotate-180')}
        />
      </span>
    </button>
    {isOpen && (
      <div className="px-4 lg:px-6 pb-4 lg:pb-6 pt-4 border-t border-gray-100 dark:border-gray-700">{children}</div>
    )}
  </section>
);

/**
 * Two-step delete: the trash icon only ARMS the button, the red confirm actually deletes. No
 * window.confirm — an Enter keypress dismisses that dialog, and these rows do not come back.
 * `warning` is deliberately shown only once armed, where it is read instead of skimmed past.
 */
const ConfirmDeleteButton = ({
  deleteLabel,
  confirmLabel,
  cancelLabel,
  warning,
  isDeleting,
  onDelete,
}: {
  deleteLabel: string;
  confirmLabel: string;
  cancelLabel: string;
  warning?: string;
  isDeleting: boolean;
  onDelete: () => void;
}) => {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <button
        type="button"
        onClick={() => setArmed(true)}
        aria-label={deleteLabel}
        title={deleteLabel}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-lg transition-colors shrink-0',
          'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20',
          'text-xs'
        )}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {warning && <span className="text-xs text-red-600 dark:text-red-400">{warning}</span>}
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-lg transition-colors shrink-0',
          'bg-red-600 dark:bg-red-600 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-700',
          'disabled:opacity-40 disabled:cursor-not-allowed text-xs', semiBoldText
        )}
      >
        {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        {confirmLabel}
      </button>
      <button
        type="button"
        onClick={() => setArmed(false)}
        disabled={isDeleting}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-lg transition-colors shrink-0',
          'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
          primaryText,
          'disabled:opacity-40 disabled:cursor-not-allowed text-xs'
        )}
      >
        <X className="w-3.5 h-3.5" />
        {cancelLabel}
      </button>
    </span>
  );
};

/** One labelled line in a request's details panel. */
const Detail = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="min-w-0">
    <p className={cn(tertiaryText, 'text-xs font-medium mb-0.5')}>{label}</p>
    <div className={cn(primaryText, smallTextSize, 'break-words')}>{value}</div>
  </div>
);

/** A prefill row poured back into the create form so it can be edited in place. */
const formFromPrefill = (prefill: DemoPrefillSummary): PrefillFormState => ({
  slug: '',
  businessName: prefill.businessName ?? '',
  restaurantCount: prefill.restaurantCount !== null ? String(prefill.restaurantCount) : '',
  restaurantCount20Plus: prefill.restaurantCount20Plus,
  revenueRange: prefill.revenueRange ?? '',
  contactName: prefill.contactName ?? '',
  email: prefill.email ?? '',
  phone: prefill.phone ?? '',
  notes: prefill.notes ?? '',
});

const AdminDemoLinksPage = () => {
  const { t, i18n } = useTranslation();

  const [form, setForm] = useState<PrefillFormState>(EMPTY_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  /** Non-null puts the form in edit mode: the ref is fixed, submit saves instead of creating. */
  const [editing, setEditing] = useState<DemoPrefillSummary | null>(null);
  const createSectionRef = useRef<HTMLDivElement>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [prefills, setPrefills] = useState<DemoPrefillSummary[]>([]);
  const [requests, setRequests] = useState<DemoRequestSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [visiblePrefills, setVisiblePrefills] = useState(PAGE_SIZE);
  const [visibleRequests, setVisibleRequests] = useState(PAGE_SIZE);
  // Both collapsed on load so the requests list is the only expanded content on the page.
  const [showCreate, setShowCreate] = useState(false);
  const [showPrefills, setShowPrefills] = useState(false);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [loadedPrefills, loadedRequests] = await Promise.all([getDemoPrefills(), getDemoRequests()]);
      setPrefills(loadedPrefills);
      setRequests(loadedRequests);
    } catch (error) {
      console.error('Failed to load demo links data', error);
      setLoadError(t('adminDemoLinks.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const patchForm = (patch: Partial<PrefillFormState>) => setForm(prev => ({ ...prev, ...patch }));

  const slug = finalizeSlug(form.slug);
  const countValue = form.restaurantCount.trim() === '' ? null : Number(form.restaurantCount);
  const countInvalid =
    !form.restaurantCount20Plus &&
    countValue !== null &&
    (!Number.isInteger(countValue) || countValue < 1 || countValue > 20);
  const emailInvalid = form.email.trim().length > 0 && !isValidEmail(form.email);
  // Editing keeps the existing ref, so the slug requirement only applies when creating.
  const canSubmit = (editing !== null || slug.length > 0) && !countInvalid && !emailInvalid && !isCreating;

  /** The fields both create and edit send, read straight off the form. */
  const prefillPayload = () => {
    const orNull = (value: string) => (value.trim().length > 0 ? value.trim() : null);
    return {
      businessName: orNull(form.businessName),
      restaurantCount: form.restaurantCount20Plus ? null : countValue,
      restaurantCount20Plus: form.restaurantCount20Plus,
      revenueRange: form.revenueRange === '' ? null : form.revenueRange,
      contactName: orNull(form.contactName),
      email: orNull(form.email),
      phone: orNull(form.phone),
      notes: orNull(form.notes),
    };
  };

  const startEditing = (prefill: DemoPrefillSummary) => {
    setEditing(prefill);
    setForm(formFromPrefill(prefill));
    setCreateError(null);
    setCreatedUrl(null);
    setShowCreate(true);
    // The form sits above the table the Edit button was clicked in, so bring it into view.
    requestAnimationFrame(() => createSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const cancelEditing = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setCreateError(null);
  };

  /**
   * Collapsing the section drops edit mode. Leaving it armed is the dangerous half: reopening
   * later looks like a blank create form but would still save over the link you had loaded.
   */
  const toggleCreateSection = () => {
    if (showCreate && editing) cancelEditing();
    setShowCreate(open => !open);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsCreating(true);
    setCreateError(null);
    setCreatedUrl(null);

    try {
      if (editing) {
        await updateDemoPrefill(editing.id, prefillPayload());
        setEditing(null);
        setForm(EMPTY_FORM);
        setShowCreate(false);
      } else {
        const created = await createDemoPrefill({ slug, ...prefillPayload() });
        setCreatedUrl(created.url);
        setForm(EMPTY_FORM);
      }
      load();
    } catch (error) {
      console.error('Failed to save demo prefill', error);
      setCreateError(
        error instanceof Error ? error.message : t(editing ? 'adminDemoLinks.updateFailed' : 'adminDemoLinks.createFailed')
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeletePrefill = async (prefill: DemoPrefillSummary) => {
    setDeletingId(prefill.id);
    setDeleteError(null);
    try {
      await deleteDemoPrefill(prefill.id);
      // A link being edited that just got deleted would save into nothing — drop the edit too.
      if (editing?.id === prefill.id) cancelEditing();
      load();
    } catch (error) {
      console.error('Failed to delete demo prefill', error);
      setDeleteError(error instanceof Error ? error.message : t('adminDemoLinks.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteRequest = async (request: DemoRequestSummary) => {
    setDeletingId(request.id);
    setDeleteError(null);
    try {
      await deleteDemoRequest(request.id);
      setExpandedRequestId(current => (current === request.id ? null : current));
      load();
    } catch (error) {
      console.error('Failed to delete demo request', error);
      setDeleteError(error instanceof Error ? error.message : t('adminDemoLinks.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const revenueLabel = (range: RevenueRange | null) => (range ? t(`bookDemo.revenue.options.${range}`) : '—');

  const countLabel = (count: number | null, twentyPlus: boolean) =>
    twentyPlus ? t('bookDemo.locations.twentyPlus') : count !== null ? String(count) : '—';

  const formatDate = (iso: string | null) => (iso ? getTableFormattedDate(new Date(iso)) : '—');

  const formatDateTime = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString(i18n.language, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const statusLabel = (status: DemoRequestSummary['status']) => t(`adminDemoLinks.status.${status}`);

  /**
   * How far off a booked demo is. No relative-time helper exists in the repo, so this leans on
   * the platform's Intl.RelativeTimeFormat, which already localises "in 3 days" / "tomorrow".
   */
  const meetingCountdown = (iso: string): { label: string; past: boolean } => {
    const target = new Date(iso);
    const diffMs = target.getTime() - Date.now();
    if (diffMs <= 0) return { label: t('adminDemoLinks.requests.relative.done'), past: true };

    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 60) return { label: rtf.format(minutes, 'minute'), past: false };

    const hours = Math.round(diffMs / 3600000);
    if (hours < 6) return { label: rtf.format(hours, 'hour'), past: false };

    // Compare calendar days, not 24h blocks, so "today" means today to the reader.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTarget = new Date(target);
    startOfTarget.setHours(0, 0, 0, 0);
    const days = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);

    if (days === 0) {
      const time = target.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
      return { label: t('adminDemoLinks.requests.relative.today', { time }), past: false };
    }
    return { label: rtf.format(days, 'day'), past: false };
  };

  const CountdownChip = ({ iso }: { iso: string }) => {
    const { label, past } = meetingCountdown(iso);
    return (
      <span
        className={cn(
          'inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
          past
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
        )}
      >
        {label}
      </span>
    );
  };

  /** Readable challenge names, reusing the wizard's own copy. */
  const challengeLabels = (challenges: string[]) =>
    challenges.length ? challenges.map(id => t(`bookDemo.challenges.options.${id}.title`)).join(', ') : '—';

  /** Prefill links are loaded anyway, so show the human-readable ref instead of the raw uuid. */
  const prefillRefFor = (request: DemoRequestSummary) =>
    request.prefillId ? prefills.find(p => p.id === request.prefillId)?.ref ?? request.prefillId : '—';

  const linkOrDash = (url: string | null, label: string) =>
    url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">
        {label}
      </a>
    ) : (
      '—'
    );

  /**
   * Edit + delete for one prefill link, shared by the desktop row and the mobile card.
   *
   * A render function, NOT a nested component: a component declared in this body gets a fresh
   * identity every render, so React would remount it — and the armed state inside
   * ConfirmDeleteButton would reset the moment anything else on the page changed.
   */
  const renderPrefillActions = (prefill: DemoPrefillSummary) => (
    <span className="flex flex-wrap items-center justify-end gap-1">
      <button
        type="button"
        onClick={() => startEditing(prefill)}
        aria-label={t('adminDemoLinks.prefills.edit')}
        title={t('adminDemoLinks.prefills.edit')}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-lg transition-colors shrink-0',
          'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
          primaryText,
          'text-xs'
        )}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <ConfirmDeleteButton
        deleteLabel={t('adminDemoLinks.prefills.delete')}
        confirmLabel={t('adminDemoLinks.confirmDelete')}
        cancelLabel={t('adminDemoLinks.cancelDelete')}
        warning={t('adminDemoLinks.prefills.deleteWarning')}
        isDeleting={deletingId === prefill.id}
        onDelete={() => handleDeletePrefill(prefill)}
      />
    </span>
  );

  /** Render function for the same reason as renderPrefillActions — it holds a delete button. */
  const renderRequestDetails = (request: DemoRequestSummary) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Detail label={t('adminDemoLinks.details.contactName')} value={request.contactName ?? '—'} />
      <Detail label={t('adminDemoLinks.details.phone')} value={request.phone ?? '—'} />
      <Detail label={t('adminDemoLinks.details.challenges')} value={challengeLabels(request.challenges)} />
      <Detail label={t('adminDemoLinks.details.locale')} value={request.locale ?? '—'} />
      <Detail label={t('adminDemoLinks.details.prefillRef')} value={prefillRefFor(request)} />
      <Detail label={t('adminDemoLinks.details.created')} value={formatDateTime(request.createdAt)} />
      <Detail label={t('adminDemoLinks.details.status')} value={statusLabel(request.status)} />
      <Detail
        label={t('adminDemoLinks.details.calendlyLinks')}
        value={
          <span className="flex gap-3">
            {linkOrDash(request.calendlyRescheduleUrl, t('adminDemoLinks.details.reschedule'))}
            {linkOrDash(request.calendlyCancelUrl, t('adminDemoLinks.details.cancel'))}
          </span>
        }
      />
      {/* Deleting a lead is only reachable once its row is expanded — one step before the arming. */}
      <div className="sm:col-span-2 pt-3 border-t border-gray-200 dark:border-gray-700">
        <ConfirmDeleteButton
          deleteLabel={t('adminDemoLinks.requests.delete')}
          confirmLabel={t('adminDemoLinks.confirmDelete')}
          cancelLabel={t('adminDemoLinks.cancelDelete')}
          warning={t(
            request.status === 'booked' && request.meetingStartTime
              ? 'adminDemoLinks.requests.deleteWarningBooked'
              : 'adminDemoLinks.requests.deleteWarning'
          )}
          isDeleting={deletingId === request.id}
          onDelete={() => handleDeleteRequest(request)}
        />
      </div>
    </div>
  );

  const toggleRequest = (id: string) => setExpandedRequestId(current => (current === id ? null : id));

  const pagedPrefills = prefills.slice(0, visiblePrefills);
  const hasMorePrefills = prefills.length > visiblePrefills;
  const pagedRequests = requests.slice(0, visibleRequests);
  const hasMoreRequests = requests.length > visibleRequests;

  const loadMoreButtonClass = cn(
    'mt-3 mx-auto block px-3 py-1.5 rounded-lg transition-colors',
    'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
    primaryText,
    'text-xs'
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ── Create / edit a prefill link (collapsed by default) ────────── */}
        <div ref={createSectionRef}>
          <CollapsibleSection
            title={editing ? t('adminDemoLinks.edit.title', { ref: editing.ref }) : t('adminDemoLinks.create.title')}
            isOpen={showCreate}
            onToggle={toggleCreateSection}
            action={
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg',
                  'bg-purple-600 dark:bg-purple-600 text-white dark:text-white',
                  'text-xs',
                  semiBoldText
                )}
              >
                {showCreate ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {t(showCreate ? 'adminDemoLinks.create.close' : 'adminDemoLinks.create.open')}
              </span>
            }
          >
          <p className={cn(secondaryText, smallTextSize, 'mb-4')}>
            {editing ? t('adminDemoLinks.edit.subtitle') : t('adminDemoLinks.create.subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="demoPrefillSlug" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {editing ? t('adminDemoLinks.edit.ref') : t('adminDemoLinks.create.slug')}
              </label>
              <input
                id="demoPrefillSlug"
                type="text"
                value={editing ? editing.ref : form.slug}
                onChange={e => patchForm({ slug: sanitizeSlugWhileTyping(e.target.value) })}
                placeholder={t('adminDemoLinks.create.slugPlaceholder')}
                disabled={editing !== null}
                className={cn(formInputClass, editing && 'font-mono')}
              />
              <p className={cn(tertiaryText, 'text-xs mt-1')}>
                {editing
                  ? t('adminDemoLinks.edit.refLocked')
                  : slug
                    ? t('adminDemoLinks.create.refPreview', { slug })
                    : t('adminDemoLinks.create.slugHint')}
              </p>
            </div>

            <div>
              <label htmlFor="demoPrefillBusiness" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.businessName')}
              </label>
              <input
                id="demoPrefillBusiness"
                type="text"
                value={form.businessName}
                onChange={e => patchForm({ businessName: e.target.value })}
                className={formInputClass}
              />
            </div>

            <div>
              <label htmlFor="demoPrefillCount" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.restaurantCount')}
              </label>
              <input
                id="demoPrefillCount"
                type="number"
                min={1}
                max={20}
                value={form.restaurantCount}
                disabled={form.restaurantCount20Plus}
                onChange={e => patchForm({ restaurantCount: e.target.value })}
                className={formInputClass}
              />
              <label className={cn(secondaryText, 'flex items-center gap-2 mt-2 text-xs cursor-pointer')}>
                <input
                  type="checkbox"
                  checked={form.restaurantCount20Plus}
                  onChange={e => patchForm({ restaurantCount20Plus: e.target.checked, restaurantCount: '' })}
                />
                {t('adminDemoLinks.create.twentyPlus')}
              </label>
              {countInvalid && (
                <p className="text-xs mt-1 text-red-600 dark:text-red-400">{t('adminDemoLinks.create.countInvalid')}</p>
              )}
            </div>

            <div>
              <label htmlFor="demoPrefillRevenue" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.revenueRange')}
              </label>
              <select
                id="demoPrefillRevenue"
                value={form.revenueRange}
                onChange={e => patchForm({ revenueRange: e.target.value as '' | RevenueRange })}
                className={formInputClass}
              >
                <option value="">{t('adminDemoLinks.create.revenueUnknown')}</option>
                {REVENUE_RANGE_OPTIONS.map(range => (
                  <option key={range} value={range}>
                    {t(`bookDemo.revenue.options.${range}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="demoPrefillContact" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.contactName')}
              </label>
              <input
                id="demoPrefillContact"
                type="text"
                value={form.contactName}
                onChange={e => patchForm({ contactName: e.target.value })}
                className={formInputClass}
              />
            </div>

            <div>
              <label htmlFor="demoPrefillEmail" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.email')}
              </label>
              <input
                id="demoPrefillEmail"
                type="email"
                value={form.email}
                onChange={e => patchForm({ email: e.target.value })}
                className={formInputClass}
              />
              {emailInvalid && (
                <p className="text-xs mt-1 text-red-600 dark:text-red-400">{t('adminDemoLinks.create.emailInvalid')}</p>
              )}
            </div>

            <div>
              <label htmlFor="demoPrefillPhone" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.phone')}
              </label>
              <input
                id="demoPrefillPhone"
                type="tel"
                value={form.phone}
                onChange={e => patchForm({ phone: e.target.value })}
                className={formInputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="demoPrefillNotes" className={cn(tertiaryText, 'block text-xs font-medium mb-1')}>
                {t('adminDemoLinks.create.notes')}
              </label>
              <textarea
                id="demoPrefillNotes"
                rows={2}
                value={form.notes}
                onChange={e => patchForm({ notes: e.target.value })}
                className={cn(formInputClass, 'resize-none')}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button type="button" onClick={handleSubmit} disabled={!canSubmit} className={primaryButtonClass}>
              {isCreating && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
              {t(editing ? 'adminDemoLinks.edit.submit' : 'adminDemoLinks.create.submit')}
            </button>
            {editing && (
              <button type="button" onClick={cancelEditing} disabled={isCreating} className={secondaryButtonClass}>
                {t('adminDemoLinks.edit.cancel')}
              </button>
            )}
            {createError && <span className={cn(smallTextSize, 'text-red-600 dark:text-red-400')}>{createError}</span>}
          </div>

          {createdUrl && (
            <div
              className={cn(
                'mt-4 p-3 rounded-lg border flex items-center justify-between gap-3',
                'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
              )}
            >
              <div className="min-w-0">
                <p className={cn(tertiaryText, 'text-xs font-medium mb-0.5')}>{t('adminDemoLinks.create.created')}</p>
                <p className={cn(primaryText, smallTextSize, 'break-all')}>{createdUrl}</p>
              </div>
              <CopyButton value={createdUrl} label={t('adminDemoLinks.copy')} copiedLabel={t('adminDemoLinks.copied')} />
            </div>
          )}
          </CollapsibleSection>
        </div>

        {deleteError && (
          <div className={cn(cardClass, 'p-4')}>
            <span className={cn(smallTextSize, 'text-red-600 dark:text-red-400')}>{deleteError}</span>
          </div>
        )}

        {loadError && (
          <div className={cn(cardClass, 'p-4 flex items-center justify-between gap-3')}>
            <span className={cn(smallTextSize, 'text-red-600 dark:text-red-400')}>{loadError}</span>
            <button type="button" onClick={load} className={primaryButtonClass}>
              {t('adminDemoLinks.retry')}
            </button>
          </div>
        )}

        {isLoading && (
          <div className={cn(cardClass, 'p-6 flex items-center justify-center gap-2', secondaryText, smallTextSize)}>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('adminDemoLinks.loading')}
          </div>
        )}

        {/* ── Prefill links (collapsed by default) ──────────────────────── */}
        {!isLoading && !loadError && (
          <CollapsibleSection
            title={t('adminDemoLinks.prefills.title', { count: prefills.length })}
            isOpen={showPrefills}
            onToggle={() => setShowPrefills(open => !open)}
          >
            {prefills.length === 0 ? (
              <p className={cn(secondaryText, smallTextSize)}>{t('adminDemoLinks.prefills.empty')}</p>
            ) : (
              <ResponsiveTable
                desktop={
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className={cn('border-b', borderPrimary)}>
                          <th className={headerCellClass}>{t('adminDemoLinks.prefills.columnRef')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.prefills.columnBusiness')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.prefills.columnRevenue')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.prefills.columnCreated')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.prefills.columnUsed')}</th>
                          <th className={cn(headerCellClass, 'text-right')}>
                            {t('adminDemoLinks.prefills.columnActions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedPrefills.map(prefill => (
                          <tr key={prefill.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                            <td className={cellClass}>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs break-all">{prefill.ref}</span>
                                <CopyButton value={prefill.url} label={t('adminDemoLinks.copy')} copiedLabel={t('adminDemoLinks.copied')} />
                              </div>
                            </td>
                            <td className={cellClass}>{prefill.businessName ?? '—'}</td>
                            <td className={cellClass}>{revenueLabel(prefill.revenueRange)}</td>
                            <td className={cn(cellClass, secondaryText, 'whitespace-nowrap')}>
                              {formatDate(prefill.createdAt)}
                            </td>
                            <td className={cellClass}>
                              <UsedBadge
                                usedAt={prefill.usedAt}
                                usedLabel={t('adminDemoLinks.prefills.used')}
                                unusedLabel={t('adminDemoLinks.prefills.unused')}
                              />
                            </td>
                            <td className={cellClass}>
                              {renderPrefillActions(prefill)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* Mobile gets the same affordance from MobileCardList's load-more mode. */}
                    {hasMorePrefills && (
                      <button
                        type="button"
                        onClick={() => setVisiblePrefills(current => current + PAGE_SIZE)}
                        className={loadMoreButtonClass}
                      >
                        {t('adminDemoLinks.loadMore')}
                      </button>
                    )}
                  </div>
                }
                mobile={
                  <MobileCardList
                    items={pagedPrefills}
                    keyOf={prefill => prefill.id}
                    onLoadMore={() => setVisiblePrefills(current => current + PAGE_SIZE)}
                    hasMore={hasMorePrefills}
                    loadMoreLabel={t('adminDemoLinks.loadMore')}
                    renderCard={prefill => (
                      <div className={cn('border rounded-xl p-3', borderPrimary)}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className={cn(primaryText, 'font-mono text-xs min-w-0 break-all')}>{prefill.ref}</span>
                          <UsedBadge
                            usedAt={prefill.usedAt}
                            usedLabel={t('adminDemoLinks.prefills.used')}
                            unusedLabel={t('adminDemoLinks.prefills.unused')}
                          />
                        </div>
                        <div className="flex mb-2">
                          <MobileCardField
                            label={t('adminDemoLinks.prefills.columnBusiness')}
                            value={prefill.businessName ?? '—'}
                            divider
                          />
                          <MobileCardField
                            label={t('adminDemoLinks.prefills.columnRevenue')}
                            value={revenueLabel(prefill.revenueRange)}
                          />
                        </div>
                        <div className="flex items-end justify-between gap-2">
                          <MobileCardField
                            label={t('adminDemoLinks.prefills.columnCreated')}
                            value={formatDate(prefill.createdAt)}
                          />
                          <span className="flex items-center gap-1">
                            <CopyButton value={prefill.url} label={t('adminDemoLinks.copy')} copiedLabel={t('adminDemoLinks.copied')} />
                            {renderPrefillActions(prefill)}
                          </span>
                        </div>
                      </div>
                    )}
                  />
                }
              />
            )}
          </CollapsibleSection>
        )}

        {/* ── Captured demo requests ────────────────────────────────────── */}
        {!isLoading && !loadError && (
          <section className={cn(cardClass, 'p-4 lg:p-6')}>
            <h2 className={cn(primaryText, 'text-lg', semiBoldText, 'mb-4')}>
              {t('adminDemoLinks.requests.title', { count: requests.length })}
            </h2>

            {requests.length === 0 ? (
              <p className={cn(secondaryText, smallTextSize)}>{t('adminDemoLinks.requests.empty')}</p>
            ) : (
              <ResponsiveTable
                desktop={
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className={cn('border-b', borderPrimary)}>
                          <th className={cn(headerCellClass, 'w-8')} />
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnDate')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnBusiness')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnCount')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnRevenue')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnStatus')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnMeeting')}</th>
                          <th className={headerCellClass}>{t('adminDemoLinks.requests.columnEmail')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedRequests.map(request => {
                          const isExpanded = expandedRequestId === request.id;
                          return (
                            <Fragment key={request.id}>
                              <tr
                                onClick={() => toggleRequest(request.id)}
                                className={cn(
                                  'border-b border-gray-100 dark:border-gray-800 cursor-pointer',
                                  'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                                  isExpanded && 'bg-gray-50 dark:bg-gray-800/50'
                                )}
                              >
                                <td className={cellClass}>
                                  <button
                                    type="button"
                                    aria-expanded={isExpanded}
                                    aria-label={t(
                                      isExpanded ? 'adminDemoLinks.requests.collapse' : 'adminDemoLinks.requests.expand'
                                    )}
                                    onClick={e => {
                                      e.stopPropagation();
                                      toggleRequest(request.id);
                                    }}
                                    className="p-1"
                                  >
                                    <ChevronDown
                                      className={cn(
                                        'w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform',
                                        isExpanded && 'rotate-180'
                                      )}
                                    />
                                  </button>
                                </td>
                                <td className={cn(cellClass, secondaryText, 'whitespace-nowrap')}>
                                  {formatDate(request.createdAt)}
                                </td>
                                <td className={cellClass}>{request.businessName ?? '—'}</td>
                                <td className={cellClass}>
                                  {countLabel(request.restaurantCount, request.restaurantCount20Plus)}
                                </td>
                                <td className={cellClass}>{revenueLabel(request.revenueRange)}</td>
                                <td className={cellClass}>
                                  <StatusChip status={request.status} label={statusLabel(request.status)} />
                                </td>
                                <td className={cn(cellClass, secondaryText, 'whitespace-nowrap')}>
                                  <span className="flex items-center gap-2">
                                    {formatDateTime(request.meetingStartTime)}
                                    {request.status === 'booked' && request.meetingStartTime && (
                                      <CountdownChip iso={request.meetingStartTime} />
                                    )}
                                  </span>
                                </td>
                                <td className={cn(cellClass, 'break-all')}>{request.email}</td>
                              </tr>
                              {isExpanded && (
                                <tr className="border-b border-gray-100 dark:border-gray-800">
                                  <td colSpan={8} className="px-3 py-4 bg-gray-50 dark:bg-gray-800/50">
                                    {renderRequestDetails(request)}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    {hasMoreRequests && (
                      <button
                        type="button"
                        onClick={() => setVisibleRequests(current => current + PAGE_SIZE)}
                        className={loadMoreButtonClass}
                      >
                        {t('adminDemoLinks.loadMore')}
                      </button>
                    )}
                  </div>
                }
                mobile={
                  <MobileCardList
                    items={pagedRequests}
                    keyOf={request => request.id}
                    onLoadMore={() => setVisibleRequests(current => current + PAGE_SIZE)}
                    hasMore={hasMoreRequests}
                    loadMoreLabel={t('adminDemoLinks.loadMore')}
                    renderCard={request => {
                      const isExpanded = expandedRequestId === request.id;
                      return (
                        <div className={cn('border rounded-xl p-3', borderPrimary)}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className={cn(primaryText, smallTextSize, semiBoldText, 'min-w-0 truncate')}>
                              {request.businessName ?? request.email}
                            </span>
                            <StatusChip status={request.status} label={statusLabel(request.status)} />
                          </div>
                          {request.status === 'booked' && request.meetingStartTime && (
                            <div className="mb-2">
                              <CountdownChip iso={request.meetingStartTime} />
                            </div>
                          )}
                          <div className="flex mb-2">
                            <MobileCardField
                              label={t('adminDemoLinks.requests.columnDate')}
                              value={formatDate(request.createdAt)}
                              divider
                            />
                            <MobileCardField
                              label={t('adminDemoLinks.requests.columnCount')}
                              value={countLabel(request.restaurantCount, request.restaurantCount20Plus)}
                            />
                          </div>
                          <div className="flex mb-2">
                            <MobileCardField
                              label={t('adminDemoLinks.requests.columnRevenue')}
                              value={revenueLabel(request.revenueRange)}
                              divider
                            />
                            <MobileCardField
                              label={t('adminDemoLinks.requests.columnMeeting')}
                              value={formatDateTime(request.meetingStartTime)}
                            />
                          </div>
                          <MobileCardField label={t('adminDemoLinks.requests.columnEmail')} value={request.email} />

                          <button
                            type="button"
                            onClick={() => toggleRequest(request.id)}
                            aria-expanded={isExpanded}
                            className={cn(
                              'mt-3 w-full flex items-center justify-center gap-1 py-1.5 rounded-lg transition-colors',
                              'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                              primaryText,
                              'text-xs'
                            )}
                          >
                            {t(isExpanded ? 'adminDemoLinks.requests.collapse' : 'adminDemoLinks.requests.expand')}
                            <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', isExpanded && 'rotate-180')} />
                          </button>

                          {isExpanded && (
                            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                              {renderRequestDetails(request)}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                }
              />
            )}
          </section>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDemoLinksPage;
